import { Namespace, TFunction } from "i18next";

import { BusinessPersonalDetailsFormFields } from "@/components/Portal/UserAccount/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import {
  BasicCustomerData,
  SimCustomerInfo,
  SimCustomerInfoAddESIMNotAllowedReasonItem,
  SimCustomerInfoAddTripleSIMNotAllowedReasonItem,
} from "@/services/model";
import { getCountryFromId } from "@/utils/countries";
import { getLocalizedDateString } from "@/utils/dateHelpers";

export const getSalutationTranslationKey = (
  salutation: BasicCustomerData["salutation"],
) => {
  switch (salutation?.id) {
    case 1:
      return "Onboarding.sections.personal-details.form.formOfAddress.mr";
    case 2:
      return "Onboarding.sections.personal-details.form.formOfAddress.mrs";
    default:
      return "Onboarding.sections.personal-details.form.formOfAddress.other";
  }
};

export const getPhoneFromBasicCustomerDataPhone = (
  phone: BasicCustomerData["phone"] | undefined,
) => {
  if (!phone) {
    return undefined;
  }

  if (phone.countryCode && phone.areaCode && phone.cli) {
    return `${phone.countryCode} ${phone.areaCode} ${phone.cli}`;
  }

  // After a local save the object only has phone.phone (slashes used as separators).
  return phone.phone ? phone.phone.replaceAll("/", " ") : undefined;
};

/**
 * Build E.164 for PhoneInput / form defaults.
 * Backend area codes often keep the national trunk 0 (e.g. "077"); PhoneInput's
 * country masks expect E.164 without that 0, so leaving it in truncates digits.
 */
export const toE164PhoneForForm = (
  phone: BasicCustomerData["phone"] | undefined,
): string => {
  if (!phone) {
    return "+41";
  }

  if (phone.countryCode && phone.areaCode && phone.cli) {
    const area = phone.areaCode.replace(/^0+/, "");

    return `${phone.countryCode}${area}${phone.cli}`;
  }

  // "+41/077/2777861" → "+41772777861"
  if (phone.phone?.includes("/")) {
    const [country, area = "", ...rest] = phone.phone.split("/");

    return `${country}${area.replace(/^0+/, "")}${rest.join("")}`;
  }

  return phone.phone?.replaceAll("/", "") || "+41";
};

/** Strip formatting so the API receives plain E.164 (e.g. "+41772777861"). */
export const toE164PhoneForApi = (phone: string): string =>
  phone.replace(/[\s\-()/]/g, "");

export const getLocalizedBirthday = (
  birthday: string | undefined,
  language: string,
) => {
  // Ensure we have a valid date string
  if (!birthday || typeof birthday !== "string" || birthday.trim() === "") {
    return "-";
  }

  // Try to parse the date safely
  const date = new Date(birthday);
  if (isNaN(date.getTime())) {
    return "-";
  }

  return getLocalizedDateString(birthday, language);
};

export const getLanguageId = (
  t: TFunction<Namespace, undefined, Namespace>,
  baseLanguage: string,
) => {
  switch (baseLanguage) {
    case t("translation:Common.languageCodes.deCode"):
      return 1;
    case t("translation:Common.languageCodes.enCode"):
      return 2;
    case t("translation:Common.languageCodes.frCode"):
      return 3;
    default:
      return 2;
  }
};

export const transformPersonalDetailsFormToBasicCustomerData: (
  formValues: BusinessPersonalDetailsFormFields,
  data: BasicCustomerData,
  language: string,
  t: TFunction<Namespace, undefined, Namespace>,
) => BasicCustomerData = (formValues, data, language, t) => {
  const birthday = formValues.dateOfBirth ?? data.birthday;

  return {
    id: data?.id,
    customerId: data.customerId,
    salutation: {
      id:
        Number(formValues.formOfAddress) === 0
          ? 0
          : Number(formValues.formOfAddress) || data.salutation!.id,
    },
    phone: {
      id: data.phone?.id,
      phone: toE164PhoneForApi(formValues.phone),
    },
    firstName: formValues.firstName,
    lastName: formValues.lastName,
    language: { id: getLanguageId(t, language) },
    ...(birthday ? { birthday: toValidBirthdayFormat(birthday) } : {}),
    mainAddress: {
      id: data.mainAddress?.id,
      zip: formValues.zip,
      city: formValues.city,
      street: formValues.street,
      houseNumber: formValues.houseNumber,
      postalDeliveryNotes: formValues.postalDeliveryNotes,
      country: { id: formValues?.country?.ID },
    },
    ...(!!data.companyName || !!formValues.organizationName
      ? {
          companyName: formValues.organizationName || data.companyName,
          legalForm: formValues.organizationForm,
        }
      : {}),
    ...(!!formValues.organizationId && {
      registerNo: formValues.organizationId,
    }),
    ...(!!data.customerType && {
      customerType: data.customerType,
    }),
  };
};

export const transformBasicCustomerDataToPersonalDetailsForm: (
  data: BasicCustomerData,
) => Omit<BusinessPersonalDetailsFormFields, "terms"> = (data) => {
  return {
    formOfAddress: data.salutation?.id?.toString() || "0",
    firstName: data?.firstName || "Max",
    lastName: data?.lastName || "Mustermann",
    street: data?.mainAddress?.street || "",
    houseNumber: data?.mainAddress?.houseNumber || "",
    postalDeliveryNotes: data?.mainAddress?.postalDeliveryNotes || "",
    zip: data?.mainAddress?.zip || "",
    city: data?.mainAddress?.city || "",
    country: getCountryFromId(data?.mainAddress?.country.id),
    phone: toE164PhoneForForm(data?.phone),
    dateOfBirth: data.birthday
      ? (() => {
          try {
            const date = new Date(data.birthday);
            if (isNaN(date.getTime())) {
              return "";
            }

            return date.toISOString().substring(0, 10);
          } catch (error) {
            return "";
          }
        })()
      : "",
    organizationName: data?.companyName || "",
    organizationForm: data?.legalForm || { id: 0, description: "" },
    organizationId: data?.registerNo || "",
  };
};

// Registration (see RegisterForm.tsx) submits these as required-field seed
// values before the customer has provided a real address — never real data.
export const PLACEHOLDER_ADDRESS_TEXT = "Dummy";
export const PLACEHOLDER_ADDRESS_ZIP = "0000";

// firstName is a mandatory field when filling personal details. If it's not
// there, the customer hasn't filled their personal details.
export const hasValidPersonalDetails = (customer: BasicCustomerData) =>
  !!customer.firstName &&
  customer.mainAddress?.street !== PLACEHOLDER_ADDRESS_TEXT &&
  customer.mainAddress?.city !== PLACEHOLDER_ADDRESS_TEXT &&
  customer.mainAddress?.zip !== PLACEHOLDER_ADDRESS_ZIP;

// Checks all mandatory fields from PersonalDetailsFormFields are present.
// Used to gate cockpit access and the onboarding redirect.
export const hasCompleteMandatoryPersonalData = (
  customer: BasicCustomerData,
): boolean => {
  // Mirrors the transform's normalization (@see transformBasicCustomerDataToPersonalDetailsForm)
  // so the "+41" default isn't mistaken for a real number.
  const normalizedPhone = toE164PhoneForForm(customer.phone);
  const birthdayDate = customer.birthday
    ? new Date(customer.birthday)
    : undefined;

  const baseFieldsComplete =
    !!customer.firstName &&
    !!customer.lastName &&
    !!birthdayDate &&
    !isNaN(birthdayDate.getTime()) &&
    normalizedPhone !== "+41" &&
    !!customer.mainAddress?.street &&
    customer.mainAddress.street !== PLACEHOLDER_ADDRESS_TEXT &&
    !!customer.mainAddress?.houseNumber &&
    !!customer.mainAddress?.zip &&
    customer.mainAddress.zip !== PLACEHOLDER_ADDRESS_ZIP &&
    !!customer.mainAddress?.city &&
    customer.mainAddress.city !== PLACEHOLDER_ADDRESS_TEXT;

  if (!baseFieldsComplete) {
    return false;
  }

  if (isBusinessCustomer(customer)) {
    return !!customer.companyName && (customer.legalForm?.id ?? 0) !== 0;
  }

  return true;
};

export const isBusinessCustomer = (customer: BasicCustomerData) =>
  customer?.customerType?.id === 2;

export const canOnlyBookData = (customer: BasicCustomerData) =>
  !!customer.dataOnly;

export const canAddESim = (simCustomerActions?: SimCustomerInfo): boolean =>
  !!simCustomerActions && simCustomerActions.addESIMAllowed === true;

export const canAddTripleSim = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  !!simCustomerActions && simCustomerActions.addTripleSIMAllowed === true;

/** True when the customer cannot add either a physical SIM or an eSIM. */
export const isAddDeviceFullyBlocked = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  !!simCustomerActions &&
  simCustomerActions.addESIMAllowed === false &&
  simCustomerActions.addTripleSIMAllowed === false;

export type AddDeviceBlockedReason =
  | SimCustomerInfoAddESIMNotAllowedReasonItem
  | SimCustomerInfoAddTripleSIMNotAllowedReasonItem;

/** Unique union of eSIM + triple-SIM block reasons for the add-device gate. */
export const getAddDeviceBlockedReasons = (
  simCustomerActions?: SimCustomerInfo,
): AddDeviceBlockedReason[] => {
  if (!simCustomerActions) {
    return [];
  }

  return [
    ...new Set([
      ...(simCustomerActions.addESIMNotAllowedReason ?? []),
      ...(simCustomerActions.addTripleSIMNotAllowedReason ?? []),
    ]),
  ];
};

export const cannotAddEsimBecauseIDCheckOpen = (
  simCustomerActions?: SimCustomerInfo,
) =>
  simCustomerActions?.addESIMNotAllowedReason?.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.IDCheckOpen,
  ) || false;
export const cannotAddEsimBecauseReachedMaximumSims = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  simCustomerActions?.addESIMNotAllowedReason?.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.TooManySIMs,
  ) || false;

export const cannotAddEsimBecauseBillDunning = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  simCustomerActions?.addESIMNotAllowedReason?.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.DunningLevel3,
  ) || false;

// manual payments level 4 in the dunning process
export const cannotAddEsimBecauseDunningProcessOpen = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  simCustomerActions?.addESIMNotAllowedReason?.includes(
    SimCustomerInfoAddESIMNotAllowedReasonItem.DunningProcessOpen,
  ) || false;

export const isInBillDunning = (
  simCustomerActions?: SimCustomerInfo,
): boolean =>
  cannotAddEsimBecauseBillDunning(simCustomerActions) ||
  cannotAddEsimBecauseDunningProcessOpen(simCustomerActions);

const toValidBirthdayFormat = (birthday: string): string => {
  // Ensure we have a valid date string
  if (
    typeof birthday !== "string" ||
    birthday.trim() === "" ||
    birthday === "-"
  ) {
    return "";
  }

  // If already in YYYY-MM-DD format, return as is
  if (birthday.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return birthday;
  }

  // Parse the date safely
  const date = new Date(birthday);
  if (isNaN(date.getTime())) {
    return "";
  }

  // Format as YYYY-MM-DD using fr-CA locale (which uses YYYY-MM-DD format)
  return date.toLocaleDateString("fr-CA");
};

// Birthdate is valid if
// - entered date is an actual date (better safe than sorry)
// - date is before now
// - date is no longer ago then 120 years
export const validateBirthDate = ({
  value,
  t,
}: {
  value: string;
  t: TFunction;
}) => {
  if (!value || typeof value !== "string") {
    return t("translation:label.validation.invalidBirthdate");
  }

  // Ensure the date is in YYYY-MM-DD format for HTML5 date input
  if (!value.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return t("translation:label.validation.invalidBirthdate");
  }

  const date = new Date(value);
  date.setHours(12, 0, 0, 0); // avoid timeshift by different timezone settings between frontend and backend

  if (isNaN(date.getTime())) {
    return t("translation:label.validation.invalidBirthdate");
  }

  if (Date.now() < date.getTime()) {
    return t("translation:label.validation.invalidBirthdate");
  }

  if (new Date().setFullYear(new Date().getFullYear() - 120) > date.getTime()) {
    return t("translation:label.validation.invalidBirthdate");
  }

  return true;
};
